import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function CsAndItTable2({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) return <p>No data available</p>;

  /** ------------------ DATA ------------------ **/
  const years = data.map((item) => item.year);
  const differences = data.map((item) => parseInt(item.difference));
  const totalEmployment = data.map((item) => parseInt(item.all_sexes));

  // Summary Calculations
  const totalChange = differences.reduce((a, b) => a + b, 0);
  const maxIncrease = Math.max(...differences);
  const maxDecrease = Math.min(...differences);

  const increaseYear = years[differences.indexOf(maxIncrease)];
  const decreaseYear = years[differences.indexOf(maxDecrease)];

  /** ------------------ CHART CONFIG ------------------ **/
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Employment Difference",
        data: differences,
        borderColor: "#B5EAD6",
        backgroundColor: "#B5EAD630",
        tension: 0.4,
        pointRadius: 6,
        pointBackgroundColor: differences.map((v) =>
          v >= 0 ? "#166534" : "#b91c1c"
        ),
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Year-to-Year Employment Difference (2018–2024)",
        font: { size: 15, weight: "bold" },
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  /** ------------------ PDF DOWNLOAD ------------------ **/
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("cs_it_emp_diff.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-6">
          Year-to-Year Employment Difference (2018–2024)
        </h2>

        {/* Summary Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-green-100 rounded-xl shadow text-center">
            <p className="text-sm text-gray-600 font-medium">Total Change</p>
            <p className="text-xl font-bold text-green-800">
              {totalChange.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow text-center">
            <p className="text-sm text-gray-600 font-medium">
              Largest Increase
            </p>
            <p className="text-xl font-bold text-blue-800">
              {increaseYear} (+{maxIncrease.toLocaleString()})
            </p>
          </div>

          <div className="p-4 bg-red-100 rounded-xl shadow text-center">
            <p className="text-sm text-gray-600 font-medium">Largest Decline</p>
            <p className="text-xl font-bold text-red-800">
              {decreaseYear} ({maxDecrease.toLocaleString()})
            </p>
          </div>
        </div>

        {/* Table & Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Total Employment</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-100 transition">
                      <td className="p-2 font-semibold text-gray-800 text-left">
                        {row.year}
                      </td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {Number(row.all_sexes).toLocaleString("en-US")}
                      </td>
                      <td
                        className={`p-2 font-bold ${
                          row.difference >= 0
                            ? "text-green-700"
                            : "text-red-700"
                        }`}
                        style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {Number(row.difference).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
